/* eslint-disable no-unused-vars */
import apiService, { resolveEssentialBaseUrl } from '@/utils/api-client'
import config from '@/utils/appConfig'
import store from '@/store'
import axios from 'axios'
import configApp from '@/utils/appConfig'
import { featureAccess } from '@/utils/accesses'

const axiosClient = axios.create()

axiosClient.interceptors.request.use(
  config => {
    // Mirror apiService (src/utils/api-client.js): when the unified essential
    // base URL is enabled, rewrite the per-chain essential base to the common
    // base and inject the `chain` param so the backend can resolve essential
    // token metadata (ticker). Without it the pdfgen context hits the per-chain
    // URL with no `chain` and tickers come back as "Unknown" (GLBA-13198).
    if (featureAccess('UNIFIED_ESSENTIAL_BASEURL')) {
      const { baseUrl, key } = resolveEssentialBaseUrl(config)

      config.baseURL = baseUrl

      if (key !== undefined) {
        config.params = {
          ...config.params,
          chain: key,
        }
      }
    } else {
      config.baseURL =
        config.baseURL ||
        store.state.analytics.coinData.baseUrl ||
        configApp.VUE_APP_API_BASE_URL
    }

    if (store.state.pdf.apiKey)
      config.headers['x-api-key'] = store.getters['pdf/getApiKey']

    return config
  },
  error => error,
)

export default {
  async getDefiSources(
    { commit },
    {
      enqId,
      directionUp,
      token = '',
      owner = undefined,
      // fundsName = undefined,
      // fundsRating = undefined,
      $skip = 0,
      $limit = 10,
    },
  ) {
    try {
      const data = axiosClient.get(`report/defi/${enqId}/sources`, {
        params: {
          enqId,
          directionUp,
          token,
          $skip,
          $limit,
          owner,
          'funds[rating]': 2,
          '$sort[amount]': -1,
        },
      })
      return data
    } catch (error) {
      console.error(error)
    }
  },
  getDefiReportById: async function (
    { commit },
    { enqId, searchingParams },
  ) {
    try {
      // return apiService.get(`report/counter-party/${enqId}`)
      return axiosClient.get(`report/defi/${enqId}`)
    } catch (error) {
      console.error(error)
    }
  },
  async getEntityList({ commit }, { ids, searchAutocomplete, $limit, $skip, filterTypeId, filterTagIds, score, name, names, aliasName, includeLogo }) {
    try {
      if (featureAccess('ENTITY')) {
        const params = { $limit, $skip }

        if (name) {
          params["name"] = `${name}`
        }
        if (ids) {
          params["ids"] = ids
        }
        if (names) {
          params["name"] = names
        }
        if (score) {
          params["score"] = score
        }
        if (filterTagIds) {
          params["tags._id"] = filterTagIds
        }
        if (filterTypeId) {
          params["type._id"] = filterTypeId
        }
        if (searchAutocomplete) {
          params["name"] = `.*${searchAutocomplete}.*`
        }
        if (aliasName) {
          params["alias.value"] = `.*${aliasName}.*`
        }

        const projectFields = {
          name: 1,
          type: 1,
          id: 1
        };

        if (includeLogo) {
          projectFields.logo = 1;
        }

        const data = axiosClient.get(`/entity`, {
          params: {
            ...params,
            '$project[name]': 1,
            '$project[type]': 1,
            '$project[id]': 1,
            '$project[logo]': 1,
          },
          baseURL: config.VUE_APP_LABEL_API_URL,
        })

        return data
      }

    } catch (error) {
      console.error(error)
    }
  },
  async getEthAddressInfo({ commit }, { address }) {
    try {
      const data = axiosClient.get(`explorer/getAddressInfo/${address}`)
      return data
    } catch (error) {
      console.error(error)
    }
  },
  async getAddressBalance({ commit }, { address, contract }) {
    try {
      const data = axiosClient.get(`explorer/getBalance/${address}`, {
        params: {
          contract: contract === 'all' ? undefined : contract,
        },
      })

      return data
    } catch (error) {
      console.error(error)
    }
  },
  async getReportAddress(
    { commit },
    {
      address,
      direction = config.VUE_APP_SCORE_REQUEST_DIRECTION,
      token,
      tokenTicker,
      totalFunds,
      alerts,
      isMonitoring,
      shallow = config.VUE_APP_SHALLOW,
      alertEnable,
      ...extraParams
    },
  ) {
    const data = axiosClient.get(`report/address/${address}`, {
      params: {
        ...extraParams,
        shallow,
        direction,
        isMonitoring,
        token: token === 'all' || token === undefined ? 'supported' : token,
        tokenTicker,
        alertEnable,
        // $project: { totalFunds, alerts },
      },
    })

    return data
  },
  async getTokens({ commit }, { supported = true, token }) {
    try {
      const data = axiosClient.get(`token`, {
        params: {
          token,
          supported: supported || undefined,
          $limit: supported ? 1000 : undefined,
        },
      })

      return data
    } catch (error) {
      console.error(error)
    }
  },
  async getAddressMetrics(
    { commit },
    { startRangeDate, endRangeDate, address, contract = '', type = 'main' },
  ) {
    try {
      const data = axiosClient.get(
        `explorer/addressMetrics/${
          store.state.analytics.coinData.key === 'trx'
            ? address
            : address.toLowerCase()
        }`,
        {
          params: {
            contract: contract === 'all' ? '' : contract,
            type,
            startRangeDate: startRangeDate || undefined,
            endRangeDate: endRangeDate || undefined,
          },
        },
      )

      return data
    } catch (error) {
      console.error(error)
    }
  },
  async getAddressTokens({ commit }, { address, supported = true }) {
    try {
      const data = axiosClient.get(`explorer/getAddressTokens/${address}`, {
        params: {
          supported: supported || undefined,
          $limit: supported ? 1000 : undefined,
        },
      })

      return data
    } catch (error) {
      console.error(error)
    }
  },
  async getTransactionTokens({ commit }, { tx, supported = true }) {
    try {
      const data = axiosClient.get(`explorer/getTransactionTokens/${tx}`, {
        params: { supported: supported || undefined },
      })

      return data
    } catch (error) {
      console.error(error)
    }
  },
  async getTxEthData(
    { commit },
    {
      tx,
      currentDate = undefined,
      contract = undefined,
      startRangeDate = undefined,
      endRangeDate = undefined,
      sort = undefined,
      limit = undefined,
      page = undefined,
      only = undefined,
      address,
    },
  ) {
    try {
      const data = await axiosClient.get(`explorer/txBrowserIO`, {
        params: {
          tx_hash: tx,
          startRangeDate,
          endRangeDate,
          contract,
          currentDate,
          sort,
          limit,
          page,
          only,
          address,
        },
      })

      return data
    } catch (error) {
      console.error(error)
    }
  },
  async getReportTx(
    { commit },
    {
      tx_hash,
      direction = config.VUE_APP_SCORE_REQUEST_DIRECTION,
      token,
      tokenTicker,
      totalFunds,
      alertEnable,
      ...extraParams
    },
  ) {
    try {
      const data = await axiosClient.get(`report/tx_hash/${tx_hash}`, {
        params: {
          ...extraParams,
          direction,
          token: token || '',
          tokenTicker,
          alertEnable,
          // $project: { totalFunds },
        },
      })
      return data
      // eslint-disable-next-line no-empty
    } finally {
    }
  },
  async getTransactionInfo(
    { commit },
    {
      txHash,
      isAddress = false,
      only = undefined,
      sortByAddress = undefined,
      inputsPerPage = only === 'outputs' ? 1000 : 20,
      outputsPerPage = only === 'inputs' ? 1000 : 20,
      inputsSkip = 0,
      outputsSkip = 0,
    },
  ) {
    try {
      const data = axiosClient.get(
        `explorer/txBrowserIO?${isAddress ? 'address' : 'tx_hash'}=${txHash}`,
        {
          params: {
            only,
            // isAddress,
            inputsPerPage,
            outputsPerPage,
            inputsSkip,
            outputsSkip,
            sortByAddress,
          },
        },
      )

      return data
    } catch (error) {
      console.error(error)
    }
  },
  async getAddressData({ commit }, { address, timestamp = undefined }) {
    try {
      const data = await axiosClient.get(`explorer/getAddressInfo/${address}`, {
        params: { timestamp },
      })

      return data
    } catch (error) {
      console.error(error)
    }
  },
}
